import React, {Component} from 'react'

import {
    Grid,
    GridCell,
    DataCell,
    DataTable,
    DataBody,
    DataHeader,
    DataRow,
    Spinner,
    css,
    TextInput,
    Select,
    Button,
    Icon,
    Theme,
} from "@bdfx/mergado-ui-kit"

import {iconDisabledColor} from "template-edit/components/tools-panel";
import { MergadoFilterType, QueriesInfo, ops } from "./mergado-queries";
import {superadminGetMergadoRulesInfo} from "app/helpers/api";
import DoubleScrollBar from 'app/components/double-scroll-bar';
import trans from 'trans';
import { AxiosResponse } from 'axios';
import { debounce } from 'lodash';

export type RulesInfo = {
    id: number
    name: string
    project_id: string
    project_element_id: string
    element_path: string
    type: string
    priority: string | number
    applies: boolean
    is_deletable: boolean
    is_movable: boolean
    is_editable: boolean
    is_pausable: boolean
    data: any
    queries: QueriesInfo[]
}

interface Props {
    selectedProjectId: number
}

interface State {
    rulesInfo: RulesInfo[]
    isLoading: boolean
    fields: string[]
    filter: MergadoFilterType[]
    editFilterIndex: number
}

const apiFields = [
    'id',
    'name',
    'project_id',
    'project_element_id',
    'type',
    'priority',
    'applies',
    'is_deletable',
    'is_movable',
    'is_editable',
    'is_pausable',
    'data',
    'queries',
    'element_path',
]

class MergadoRules extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            isLoading: false,
            rulesInfo: null,
            fields: ['id', 'name', 'project_id', 'priority', 'type', 'data', 'applies', 'is_editable', 'queries', 'element_path'],
            filter: [
                { field: null, op: null, value: null }
            ],
            editFilterIndex: 0,
        }
    }

    getMergadoRulesDeBounced = debounce(() => {
        const {fields, filter} = this.state
        this.getMergadoRules(fields, filter)
    }, 2000)

    getMergadoRules = (fields: string[], filter: MergadoFilterType[]) => {
        const { selectedProjectId } = this.props

        this.setState({isLoading: true})
        superadminGetMergadoRulesInfo(selectedProjectId, fields, filter)
            .then((response: AxiosResponse<any>) => {
                this.setState({rulesInfo: response.data})
            })
            .catch(error => {
                console.error(error.response)
            })
            .finally(() => {
                this.setState({isLoading: false})
            })
    }

    componentDidMount() {
        const { fields, filter } = this.state

        if (this.props.selectedProjectId) {
            this.getMergadoRules(fields, filter)
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (prevProps && prevProps.selectedProjectId !== this.props.selectedProjectId && this.props.selectedProjectId) {
            this.getMergadoRules(this.state.fields, this.state.filter)
        }
    }


    render() {
        const { isLoading, rulesInfo, fields, filter, editFilterIndex } = this.state

        if (isLoading) {
            return <div>
                Loading Mergado rules <Spinner type={'bubbles'} />
            </div>
        }

        if (!rulesInfo) {
            return <span />
        }

        const selectFieldsOptions = apiFields.map((o: string) => {
            return {
                label: `${o}`,
                value: `${o}`,
            }
        })

        const selectOpsOptions = ops.map((o: string) => {
            return {
                label: `${o}`,
                value: `${o}`,
            }
        })

        return (
            <div>
                <Grid cols={'repeat(5, 1fr)'}>
                    <GridCell>
                        fields: <Select
                            options={selectFieldsOptions}
                            value={fields}
                            multiple={true}
                            onChange={(value) => {
                                this.setState({ fields: value })
                                this.getMergadoRulesDeBounced()
                            }}
                        />
                    </GridCell>
                    <GridCell>field:
                        <Select
                            options={selectFieldsOptions}
                            value={filter[editFilterIndex].field}
                            onChange={(value) => {
                                value
                                let updatedFilter = [...filter]
                                updatedFilter[editFilterIndex].field = value
                                this.setState({ filter: updatedFilter })
                            }}
                            onClear={() => {
                                let updatedFilter = [...filter]
                                updatedFilter[editFilterIndex].field = null
                                this.setState({ filter: updatedFilter })
                            }}
                        />
                    </GridCell>
                    <GridCell>op:
                        <Select
                            options={selectOpsOptions}
                            value={filter[editFilterIndex].op}
                            onChange={(value) => {
                                let updatedFilter = [...filter]
                                updatedFilter[editFilterIndex].op = value
                                this.setState({ filter: updatedFilter })
                            }}
                            onClear={() => {
                                let updatedFilter = [...filter]
                                updatedFilter[editFilterIndex].op = null
                                this.setState({ filter: updatedFilter })
                            }}
                        />
                    </GridCell>
                    <GridCell>value:
                        <Grid cols={'repeat(4, max-content)'}>
                            <TextInput
                                value={typeof filter[editFilterIndex].value === 'string' ? `${filter[editFilterIndex].value}` : ''}
                                onChange={(value) => {
                                    let updatedFilter = [...filter]
                                    updatedFilter[editFilterIndex].value = value
                                    this.setState({ filter: updatedFilter })
                                }}
                                onClear={() => {
                                    let updatedFilter = [...filter]
                                    updatedFilter[editFilterIndex].value = null
                                    this.setState({ filter: updatedFilter })
                                }}
                            />
                            <Button
                                type={'void'}
                                color={'nocolor'}
                                onClick={() => {
                                    let updatedFilter = [...filter]
                                    updatedFilter[editFilterIndex].value = true
                                    this.setState({ filter: updatedFilter })
                                }}
                                icon={<Icon type="toggle-on" color={filter[editFilterIndex].value === true ? Theme.green : Theme.grey} text={'true'} />}
                            />
                            <Button
                                type={'void'}
                                color={'nocolor'}
                                onClick={() => {
                                    let updatedFilter = [...filter]
                                    updatedFilter[editFilterIndex].value = false
                                    this.setState({ filter: updatedFilter })
                                }}
                                icon={<Icon type="toggle-off" color={filter[editFilterIndex].value === false ? Theme.green : Theme.grey} text={'false'} />}
                            />
                            <Button
                                type={'void'}
                                color={'nocolor'}
                                onClick={() => {
                                    let updatedFilter = [...filter]
                                    updatedFilter[editFilterIndex].value = null
                                    this.setState({ filter: updatedFilter })
                                }}
                                icon={<Icon type="circle" color={filter[editFilterIndex].value === null ? Theme.green : Theme.grey} text={'null'} />}
                            />
                        </Grid>
                    </GridCell>
                    <GridCell style={{ alignSelf: 'center' }}>
                        <Button
                            type={'void'}
                            onClick={() => this.getMergadoRules(fields, filter)}
                            icon={<Icon type={'filter'} text={trans('filter')} />}
                        />
                    </GridCell>
                </Grid>

                <DoubleScrollBar color={Theme.blue}>
                    <DataTable onRowSelected={() => { }}>
                        <DataHeader>
                            {fields.includes('id') &&
                                <DataCell type={"header"}>rule ID</DataCell>
                            }
                            {fields.includes('name') &&
                                <DataCell type={"header"}>name</DataCell>
                            }
                            {fields.includes('queries') &&
                                <DataCell type={"header"}>queries</DataCell>
                            }
                            {fields.includes('data') &&
                                <DataCell type={"header"}>data</DataCell>
                            }
                            {fields.includes('type') &&
                                <DataCell type={"header"}>type</DataCell>
                            }
                            {fields.includes('applies') &&
                                <DataCell type={"header"}>applies</DataCell>
                            }
                            {fields.includes('element_path') &&
                                <DataCell type={"header"}>element_path</DataCell>
                            }
                            {fields.includes('project_element_id') &&
                                <DataCell type={"header"}>project_element_id</DataCell>
                            }
                            {fields.includes('priority') &&
                                <DataCell type={"header"} width={'1%'}>priority</DataCell>
                            }
                        </DataHeader>
                        <DataBody>
                            {rulesInfo.map(rule => {
                                return <DataRow dataId={'rule' + rule.id} key={'rule' + rule.id}>
                                    {fields.includes('id') &&
                                        <DataCell>{rule.id}</DataCell>
                                    }
                                    {fields.includes('name') &&
                                        <DataCell style={{ color: rule.applies ? Theme.green : iconDisabledColor }}>{rule.name}</DataCell>
                                    }
                                    {fields.includes('queries') &&
                                        <DataCell>
                                            <LargeDiv>
                                                {rule.queries &&
                                                    rule.queries.map(query => {
                                                        return <div style={{ width: '100%' }} key={'query' + query.id}>{query.id} - <span style={{
                                                            fontWeight: 'bold',
                                                            color: Theme.blue
                                                        }}>{query.name}</span> [{query.product_count}]<br /> <span
                                                            style={{ fontSize: 'smaller', fontWeight: 'bold' }}>{query.query}</span></div>
                                                    })
                                                }
                                            </LargeDiv>
                                        </DataCell>
                                    }
                                    {fields.includes('data') &&
                                        <DataCell>
                                            <LargeDiv>
                                                {JSON.stringify(rule.data)}
                                            </LargeDiv>
                                        </DataCell>
                                    }
                                    {fields.includes('type') &&
                                        <DataCell>{rule.type}</DataCell>
                                    }
                                    {fields.includes('applies') &&
                                        <DataCell>{rule.applies ? <b>true</b> : '-'}</DataCell>
                                    }
                                    {fields.includes('element_path') &&
                                        <DataCell>{rule.element_path}</DataCell>
                                    }
                                    {fields.includes('project_element_id') &&
                                        <DataCell>{rule.project_element_id}</DataCell>
                                    }
                                    {fields.includes('priority') &&
                                        <DataCell><div style={{ maxWidth: '60px', overflow: 'auto' }}>{rule.priority}</div></DataCell>
                                    }
                                </DataRow>
                            })}
                        </DataBody>
                    </DataTable>
                </DoubleScrollBar>
            </div>
        )
    }
}

const LargeDiv = css('div')({
    '@media screen and (max-width: 800px)': {
        maxWidth: '200px',
        maxHeight: '80px',
        overflow: 'auto',
    },
})

export default MergadoRules